import { useContext, useRef, useState } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import { ComboBox } from "../../../filter/ComboBox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../select/Select";
import { NumberQuery } from "../../../filter/custom/types";
import { numberQueries } from "../../../../config/filter/CustomFilterConfig";
import {
  IPersonKundenummerSetting,
  QueryFilter,
  PersonFilterKeys,
} from "../../../../interfaces/filters";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { PersonDtoFilterInput } from "../../../../interfaces/schema";
import { f } from "../../../../util/f";

const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "kundenummer";

export default function PersonKundenummerFilter() {
  // query filter context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // find the filter settings
  const settings: IPersonKundenummerSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // things
  const queries: NumberQuery[] = numberQueries;

  // refs
  const inputRef = useRef<HTMLInputElement>(null);
  const inputToRef = useRef<HTMLInputElement>(null);

  // state stuff
  const [selectedQuery, setSelectedQuery] = useState(queries[0]);
  const [isBetween, setIsBetween] = useState<boolean>(false);

  // handlers
  function addFilterEventHandler() {
    const inputValue = inputRef.current?.value;
    if (!inputValue) {
      return;
    }

    const key: IPersonKundenummerSetting["key"] = `${
      selectedQuery.query
    }-${parseInt(inputValue)}`;

    if (settings.some((set) => set.key === key)) {
      alert("Filter er allerede aktivt.");
      return;
    }

    const settingsItem: IPersonKundenummerSetting = {
      key: key,
      selectedQueryName: `${selectedQuery.displayName}`,
      displayName: `${parseInt(inputValue)}`,
      filter: { kundeNr: { [selectedQuery.query]: parseInt(inputValue) } },
    };

    const updatedSettings = [...settings, settingsItem];

    const filterString = f<PersonDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonKundenummerSetting[]
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function addBetweenFilterEventHandler() {
    const inputFromValue = inputRef.current?.value;
    const inputToValue = inputToRef.current?.value;

    if (!inputFromValue || !inputToValue) {
      return;
    }
    const betweenItem: IPersonKundenummerSetting = {
      key: `gt-${parseInt(inputFromValue)}-lt${parseInt(inputToValue)}`,
      selectedQueryName: "Mellem",
      displayName: `${parseInt(inputFromValue)} - ${parseInt(inputToValue)}`,
      filter: {
        and: [
          { kundeNr: { gt: parseInt(inputFromValue) } },
          { kundeNr: { lt: parseInt(inputToValue) } },
        ],
      },
    };

    const updatedSettings = [...settings, betweenItem];

    const filterString = f<PersonDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonKundenummerSetting[]
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterEventHandler(key: IPersonKundenummerSetting["key"]) {
    const updatedSettings = settings.filter((set) => set.key !== key);

    if (updatedSettings.length === 0) {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
      return;
    }

    const filterString = f<PersonDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonKundenummerSetting[]
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  return (
    <AccordionFilterItem
      title="Kundenummer"
      value="kundenummer"
      isActive={settings.length > 0 && true}
    >
      <div className="c-applied-filter-group">
        {settings.map((set: IPersonKundenummerSetting) => {
          return (
            <AppliedFilterItem
              key={set.key}
              labelPrefix={set.selectedQueryName}
              label={set.displayName}
              removeFilterHandler={() => {
                removeFilterEventHandler(set.key);
              }}
            />
          );
        })}
      </div>
      <ComboBox>
        <Select
          defaultValue={selectedQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsBetween(true);
              return;
            } else {
              setIsBetween(false);
            }

            const query = queries.find((q) => q.query === value);
            if (query) {
              setSelectedQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {queries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <input
          className="form-control"
          type="number"
          placeholder="Kundenummer"
          ref={inputRef}
          required
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              if (isBetween) {
                addBetweenFilterEventHandler();
              } else {
                addFilterEventHandler();
              }
            }
          }}
        />
        {isBetween && (
          <>
            -{" "}
            <input
              className="form-control"
              type="number"
              placeholder="Kundenummer"
              ref={inputToRef}
              required
              onKeyUp={(event) => {
                if (event.key === "Enter") {
                  if (isBetween) {
                    addBetweenFilterEventHandler();
                  } else {
                    addFilterEventHandler();
                  }
                }
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 btn--combo-action`}
          onClick={() => {
            if (isBetween) {
              addBetweenFilterEventHandler();
            } else {
              addFilterEventHandler();
            }
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionFilterItem>
  );
}
